/**
 * Seek bar — TASK 8.3 Step 2 (AC 6.2, AC 6.8).
 *
 * The mock's native `<input type="range" aria-label="Seek">` (L752) plus the
 * two things the mock never had:
 *
 * 1. EXPLICIT KEYBOARD HANDLING. A native range IS keyboard-operable in a
 *    browser — but only by `step`, and the mock declared none, so Left/Right
 *    moved by 1 second on a 4-minute track. AC 6.2 asks for ~5 s. `step` is
 *    therefore set, AND the arrow keys are handled explicitly, because the
 *    handler is the only version of this that is TESTABLE: jsdom implements no
 *    default action for a range's arrow keys at all, so a suite that pressed
 *    ArrowRight and asserted on `value` would pass vacuously forever. The two
 *    agree by construction — both move {@link SEEK_STEP_S}.
 *
 * 2. A SETTLING RULE. `update()` writes the position the STORE holds, and the
 *    engine holds the REQUESTED position from the moment a seek is issued
 *    until the element reports `seeked` (audio-engine.ts `pendingSeek`). So the
 *    thumb goes to where the user asked and stays there while the buffer
 *    fills, instead of snapping back to the element's stale `currentTime` —
 *    which is exactly the "restarts from 0:00" failure AC 6.8 forbids.
 *
 * 3. A THROTTLE ON THE DRAG. A native range fires `input` on every pointer
 *    move — dozens of events per drag — and each one used to reach
 *    `audio-engine.ts#seek`, which writes `element.currentTime`. Every such
 *    write ABORTS the element's in-flight range request and opens a new one
 *    against `GET /api/stream/:trackId`, which is lease-capped and deliberately
 *    exempt from the request-count rate limiter. That is the exact shape of the
 *    Critical Story 5 shipped: three rapid seeks produced a 90 s
 *    `429 STREAM_LIMIT` self-DoS. Worse, the 429 lands on the ELEMENT's opaque
 *    request, so the engine's `error` handler misreads it as a transport
 *    failure, reloads, and the track restarts at 0:00 — the precise outcome
 *    AC 6.8 forbids.
 *
 *    So: `input` is TRAILING-DEBOUNCED at {@link SEEK_COMMIT_DEBOUNCE_MS}, and
 *    `change` (pointer release / the native commit) flushes immediately, so a
 *    completed drag is never delayed. THE KEYBOARD PATH IS NOT DEBOUNCED: the
 *    arrow keys are discrete, deliberate, ~5 s apart in the worst case, and
 *    they are the accessible path — making them feel laggy to save a request
 *    the user was never going to make would be the wrong trade.
 *
 * `aria-valuetext` is added for the same reason a range needs it anywhere:
 * "137" is not a useful announcement, "2:17 of 4:03" is.
 *
 * @module
 */

import { append, el } from '../../components/primitives/dom.js';
import { formatSeconds } from '../../components/primitives/format.js';

/** AC 6.2 — one arrow press moves ~5 seconds. */
export const SEEK_STEP_S = 5;

/**
 * Trailing debounce on `input`. Long enough that a continuous drag issues one
 * seek rather than dozens, short enough that a slow scrub still feels live.
 */
export const SEEK_COMMIT_DEBOUNCE_MS = 150;

/**
 * @typedef {object} SeekBarHandle
 * @property {HTMLElement} element the `.sm-player-seek` block (input + times)
 * @property {HTMLInputElement} input the range itself
 * @property {(next: { position: number, duration: number }) => void} update
 */

/**
 * @param {{
 *   position?: number,
 *   duration?: number,
 *   step?: number,
 *   onSeek: (seconds: number) => void,
 *   debounceMs?: number,
 *   setTimer?: (fn: () => void, ms: number) => unknown,
 *   clearTimer?: (handle: unknown) => void,
 * }} options
 * @returns {SeekBarHandle}
 */
export function SeekBar(options) {
  const {
    position = 0,
    duration = 0,
    step = SEEK_STEP_S,
    onSeek,
    debounceMs = SEEK_COMMIT_DEBOUNCE_MS,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (handle) => {
      clearTimeout(handle);
    },
  } = options;

  let current = position;
  let total = duration;

  /** Pending trailing-debounce timer, and the position it would commit. */
  let commitTimer = null;
  let pendingTarget = null;

  /** Issues the pending seek now (pointer release, or the debounce elapsing). */
  function commit() {
    if (commitTimer !== null) {
      clearTimer(commitTimer);
      commitTimer = null;
    }
    if (pendingTarget === null) return;
    const target = pendingTarget;
    pendingTarget = null;
    onSeek(target);
  }

  /** Queues a drag position, replacing whatever was queued before it. */
  function queue(seconds) {
    pendingTarget = seconds;
    // Keep the THUMB live during the drag even though the transport is not:
    // the control must never feel like it is fighting the finger.
    current = seconds;
    paint();
    if (commitTimer !== null) return;
    commitTimer = setTimer(() => {
      commitTimer = null;
      commit();
    }, debounceMs);
  }

  /** The range's `max` must never be 0 or the thumb has nowhere to go. */
  const maxOf = (seconds) => Math.max(1, Math.round(seconds));

  const input = /** @type {HTMLInputElement} */ (
    el('input', {
      className: 'sm-seek',
      attrs: {
        type: 'range',
        min: '0',
        max: String(maxOf(total)),
        step: String(step),
        value: String(Math.round(current)),
        'aria-label': 'Seek',
      },
      on: {
        // `input` (not just `change`) so a drag scrubs live, as a native range
        // does everywhere except IE — but TRAILING-DEBOUNCED, so one drag is
        // one range request rather than dozens (see the module header).
        input: (event) => {
          queue(Number(/** @type {HTMLInputElement} */ (event.target).value));
        },
        // Pointer release / native commit: the drag is over, seek immediately.
        change: (event) => {
          pendingTarget = Number(/** @type {HTMLInputElement} */ (event.target).value);
          commit();
        },
        keydown: (event) => {
          const key = /** @type {KeyboardEvent} */ (event).key;
          let target = null;
          if (key === 'ArrowLeft' || key === 'ArrowDown') target = current - step;
          else if (key === 'ArrowRight' || key === 'ArrowUp') target = current + step;
          else if (key === 'Home') target = 0;
          else if (key === 'End') target = total;
          if (target === null) return;
          // Cancel the browser's own (1-step) move so the two cannot double up.
          event.preventDefault();
          // NOT debounced: discrete, deliberate, and the accessible path.
          pendingTarget = Math.max(0, Math.min(target, total));
          commit();
        },
      },
    })
  );

  const positionLabel = el('span', { text: formatSeconds(current) });
  const durationLabel = el('span', { text: formatSeconds(total) });

  const times = append(
    el('div', { className: 'sm-player-times' }),
    positionLabel,
    durationLabel,
  );
  const root = append(el('div', { className: 'sm-player-seek' }), input, times);

  function paint() {
    input.max = String(maxOf(total));
    input.value = String(Math.round(current));
    input.setAttribute(
      'aria-valuetext',
      `${formatSeconds(current)} of ${formatSeconds(total)}`,
    );
    positionLabel.textContent = formatSeconds(current);
    durationLabel.textContent = formatSeconds(total);
  }

  paint();

  return {
    element: root,
    input,
    update(next) {
      total = next.duration;
      // While a drag is queued the engine has not been told the new position
      // yet, so the store still holds the OLD one. Writing it would snap the
      // thumb backwards under the user's finger — the same class of defect as
      // the `pendingSeek` settling rule above, one layer up.
      if (pendingTarget === null) current = next.position;
      paint();
    },
  };
}
